import cn from 'utils/cn'
import FeatureIcon from 'core/components/icons/FeatureIcon'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import usePathSection from 'core/hooks/navigation/usePathSection'
import Popover from 'core/components/dialogs/Popover'
import React, { useMemo, useState } from 'react'
import useNavigation from 'core/hooks/navigation/useNavigation'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'
import useAuth from 'core/hooks/auth/useAuth'

interface AccountSwitcherProps {
  open: boolean
}

const ListItem = ({
  label,
  checked = false,
  icon = null,
  show = true,
  onClick
}: {
  label: string
  checked?: boolean
  show?: boolean
  icon?: `${IconType}` | null
  onClick: React.MouseEventHandler<HTMLDivElement>
}) => {
  return show ? (
    <div
      className="flex items-center justify-between py-2 px-1 hover:bg-secondary-1 cursor-pointer rounded-md"
      onClick={onClick}
    >
      <div
        className={cn(['flex items-center gap-2', checked && 'font-semibold'])}
      >
        {icon && <Icon name={icon} height={20} width={20} color="text-dark" />}
        {label}
      </div>
      {checked ? <Icon name="check" color="text-primary" /> : <span />}
    </div>
  ) : null
}

const AccountSwitcher = ({ open }: AccountSwitcherProps) => {
  const { push } = useNavigation()
  const {
    permissions: { hasAccessToTeamDashboard },
    hasOnlyRole
  } = useRolesAndPermissions()
  const { data: { company_name } = {} } = useCurrentTeam()
  const { data: { email, name, last } = {}, isLoading } = useUserProfile()
  const { isTeamSpace } = usePathSection()
  const [show, setShow] = useState(false)
  const { logout } = useAuth()

  const isOnlyReviewer = hasOnlyRole('isReviewer')

  const menu = useMemo(() => {
    return (
      <div className="bg-white shadow-lg rounded-md mt-[-7px] min-w-[305px] p-4">
        <div className="flex items-center justify-between flex-row gap-4">
          <span>
            You’re signed in as <span className="font-semibold">{email}</span>{' '}
            <Icon
              name="close"
              color="text-dark-4 text-xl ml-4 cursor-pointer hover:opacity-80"
            />
          </span>
        </div>
        <div className="bg-dark-1 h-px w-full my-4" />
        <div className="flex gap-3 flex-col">
          <ListItem
            checked={isTeamSpace}
            show={hasAccessToTeamDashboard}
            label="Team dashboard"
            onClick={(e) => {
              e.stopPropagation()
              push(isOnlyReviewer ? '/teams/compliance' : '/teams')
              setShow(false)
            }}
          />
          <ListItem
            checked={!isTeamSpace}
            show={hasAccessToTeamDashboard && !isOnlyReviewer}
            label="Personal dashboard"
            onClick={(e) => {
              e.stopPropagation()
              push('/dashboard')
              setShow(false)
            }}
          />
          <ListItem
            label="Log out"
            icon="logout-outline"
            onClick={() => logout()}
          />
        </div>
      </div>
    )
  }, [
    email,
    hasAccessToTeamDashboard,
    isOnlyReviewer,
    isTeamSpace,
    logout,
    push
  ])

  return (
    <Popover
      content={!isLoading ? menu : null}
      show={show}
      position="bottom-right"
      useArrow={false}
    >
      <div
        onClick={() => setShow(!show)}
        onBlur={() => setShow(false)}
        tabIndex={0}
        className="my-5 flex justify-between items-center gap-2 cursor-pointer select-none h-20"
      >
        <div className="flex items-center gap-3">
          <FeatureIcon
            name="everlance"
            height={28}
            width={30}
            className="bg-white rounded-xl p-2 hover:bg-white/80"
          />
          <div
            className={cn([
              'flex-col items-start gap-2 hover:opacity-80',
              open ? 'inline-flex' : 'hidden',
              isTeamSpace ? 'text-white' : 'text-dark'
            ])}
          >
            <div className="font-medium truncate w-[125px]">
              {isTeamSpace
                ? company_name
                : name && last
                  ? `${name} ${last}`
                  : email}
            </div>
            <div className="text-sm font-normal leading-none">
              {isTeamSpace ? 'Team dashboard' : 'Personal dashboard'}
            </div>
          </div>
        </div>
        <Icon
          name="chevron-down"
          size={14}
          className={cn([
            'text-lg hover:opacity-80',
            open ? 'block' : 'hidden',
            isTeamSpace ? 'text-white' : 'text-dark'
          ])}
        />
      </div>
    </Popover>
  )
}

export default AccountSwitcher
